/**
 * BookedEmailButton — the one explicit action that sends the booked-or-held letter to
 * everyone on the trip. Nothing else sends it. It shows who will get it before it goes,
 * and says plainly when the letter was held and nothing went out.
 */
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Mail } from "lucide-react";
import { tripsApi } from "@/lib/api";

interface Props {
  tripId: string;
}

type Tone = "ok" | "warn";

/** What the owner is told after pressing Send. Always says whether anything went out. */
export function resultMessage(r: { status: string; reason?: string; sent?: string[]; failed?: string[] }): { message: string; tone: Tone } {
  if (r.status === "sent") {
    const sent = r.sent ?? [];
    const failed = r.failed ?? [];
    return failed.length
      ? { tone: "warn", message: `Sent to ${sent.join(", ") || "no one"}. Could not reach ${failed.join(", ")}.` }
      : { tone: "ok", message: `Sent to ${sent.join(", ")}.` };
  }
  if (r.status === "already_sent") return { tone: "ok", message: "This went out a few minutes ago, so it was not sent again." };
  if (r.status === "held") return { tone: "warn", message: r.reason ?? "The letter was held. Nothing was sent." };
  if (r.status === "nothing_to_send" && r.reason === "hold_ended") {
    return { tone: "warn", message: "The hold's end time has passed. Update the booking first. Nothing was sent." };
  }
  return { tone: "warn", message: "Nothing to send yet. Nothing was sent." };
}

type Phase =
  | { kind: "idle" }
  | { kind: "confirm" }
  | { kind: "sending" }
  | { kind: "done"; message: string; tone: Tone };

export default function BookedEmailButton({ tripId }: Props) {
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const { data, isLoading, isError } = useQuery({
    queryKey: ["bookedEmail", tripId],
    queryFn: () => tripsApi.bookedEmailReadiness(tripId),
    enabled: phase.kind === "confirm",
  });

  const send = async () => {
    setPhase({ kind: "sending" });
    try {
      const r = await tripsApi.sendBookedEmail(tripId);
      setPhase({ kind: "done", ...resultMessage(r) });
    } catch {
      setPhase({ kind: "done", tone: "warn", message: "That did not go through. Nothing was sent." });
    }
  };

  if (phase.kind === "idle") {
    return (
      <button
        type="button"
        onClick={() => setPhase({ kind: "confirm" })}
        className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1 text-xs font-medium text-foreground hover:bg-muted"
      >
        <Mail className="h-3.5 w-3.5" aria-hidden="true" />
        Email these flights to the travelers
      </button>
    );
  }

  if (phase.kind === "done") {
    return (
      <p role="status" className={`text-xs ${phase.tone === "ok" ? "text-emerald-700" : "text-amber-700"}`}>
        {phase.message}
      </p>
    );
  }

  const recipients = data?.recipients ?? [];
  const ready = !!data?.ready && recipients.length > 0;
  return (
    <div className="rounded-md border border-border p-2.5 text-xs">
      {isLoading ? (
        <p className="text-muted-foreground">Checking who is on this trip&hellip;</p>
      ) : isError ? (
        <p className="text-amber-700">Could not check who is on this trip.</p>
      ) : data?.reason === "hold_ended" ? (
        <p className="text-amber-700">The hold&rsquo;s end time has passed. Update the booking first.</p>
      ) : recipients.length === 0 ? (
        <p className="text-muted-foreground">No traveler on this trip has an email address yet.</p>
      ) : (
        <p>
          This goes to <span className="font-medium">{recipients.map((p) => p.name || p.email).join(", ")}</span>. A traveler with no
          address on the trip will not get it.
        </p>
      )}
      <div className="mt-2 flex items-center gap-2">
        <button
          type="button"
          disabled={!ready || phase.kind === "sending"}
          onClick={send}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-2.5 py-1 font-medium text-primary-foreground disabled:opacity-50"
        >
          {phase.kind === "sending" ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : null}
          {phase.kind === "sending" ? "Sending" : "Send"}
        </button>
        <button
          type="button"
          disabled={phase.kind === "sending"}
          onClick={() => setPhase({ kind: "idle" })}
          className="rounded-md px-2.5 py-1 text-muted-foreground hover:bg-muted"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
